import React, { useEffect, useState, type FunctionComponent } from 'react'
import {
	cacheAuthUserIdentity,
	getEnvironment,
	isProductionOrStaging,
	logOut,
	useAuth0Cache,
	Routes,
	ModalProvider,
	ToastProvider,
} from '@borg/aemp-integrations'
import {
	DATADOG_APPLICATION_ID,
	DATADOG_CLIENT_TOKEN,
} from '@borg/aemp-integrations/src/config'
import {
	useAuth0,
	useIdentity,
	type Auth0ContextInterface,
	type Identity,
} from '@equipmentshare/auth0-react'
import { datadogRum } from '@datadog/browser-rum'
import { formatRumUser } from '@equipmentshare/audit-logger-ui'
import { Datadog, DatadogRumViewTracker } from '@borg/shared-analytics'
import { ThemeProvider } from '@mui/material/styles'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { BrowserRouter } from 'react-router'
import { createAppTheme } from '@equipmentshare/t3-mui-theme'
import { version } from '../package.json'
import { AppLoader } from './app-loader'
import { QueryClientWrapper } from './queryClientWrapper'

export const appTheme = createAppTheme()

export const App: FunctionComponent = () => {
	const [ authToken, setAuthToken ] = useState<string | null>( null )
	const auth0UserIdentity: Identity = useIdentity()
	const { userId } = auth0UserIdentity

	useEffect( () => {
		const isRumUserSet = datadogRum.getUser().hasOwnProperty( 'id' )
		if ( !isRumUserSet && userId ) {
			datadogRum.setUser( formatRumUser( auth0UserIdentity ) )
		} else if ( isRumUserSet && !userId ) {
			datadogRum.clearUser()
		}
	}, [ auth0UserIdentity, userId ] )

	useAuth0Cache()
	const { getAccessTokenSilently }: Auth0ContextInterface = useAuth0()
	const isLoading = !authToken

	useEffect( () => {
		cacheAuthUserIdentity( auth0UserIdentity )
	}, [ auth0UserIdentity ] )

	useEffect( () => {
		// We get the auth token before app loads because
		// we don't want to call a custom event on page load that is unauthenticated
		getAccessTokenSilently()
			.then( ( token: string ) => setAuthToken( token ) )
			.catch( () => logOut() )
	}, [ getAccessTokenSilently ] )

	if ( isLoading ) {
		return <AppLoader />
	}

	return (
		<QueryClientWrapper>
			<ThemeProvider theme={ appTheme }>
				<LocalizationProvider dateAdapter={ AdapterDateFns }>
					<ToastProvider>
						<ModalProvider>
							<BrowserRouter>
								<Datadog
									applicationId={ DATADOG_APPLICATION_ID }
									clientToken={ DATADOG_CLIENT_TOKEN }
									service="aemp-integrations"
									env={ getEnvironment() }
									version={ version }
									isEnabled={ isProductionOrStaging() }
								/>
								<Routes />
								<DatadogRumViewTracker isEnabled={ isProductionOrStaging() } />
							</BrowserRouter>
						</ModalProvider>
					</ToastProvider>
				</LocalizationProvider>
			</ThemeProvider>
		</QueryClientWrapper>
	)
}
